// Block model for CMS-authored blog articles.
//
// The body of a `blog_posts` row is a JSON array of these blocks. The same
// model is used by the admin block editor, the admin live preview and the
// public /blog/$slug route, so there is exactly one renderer and no drift.
//
// Client-safe: no server imports.

export type BlockType =
  | "heading"
  | "paragraph"
  | "bullets"
  | "numbers"
  | "callout"
  | "quote"
  | "table"
  | "image"
  | "faq"
  | "product"
  | "reference";

export type ProductHandle = "mag-absorb-pro" | "liver-biome-plus" | "ova-balance";

export type Block =
  | { id: string; type: "heading"; level: 2 | 3; text: string }
  | { id: string; type: "paragraph"; text: string }
  | { id: string; type: "bullets"; items: string[] }
  | { id: string; type: "numbers"; items: string[] }
  | { id: string; type: "callout"; title: string; text: string }
  | { id: string; type: "quote"; text: string; attribution: string }
  | { id: string; type: "table"; headers: string[]; rows: string[][] }
  | { id: string; type: "image"; url: string; alt: string; caption: string }
  | { id: string; type: "faq"; question: string; answer: string }
  | { id: string; type: "product"; handle: ProductHandle; blurb: string }
  | { id: string; type: "reference"; label: string; url: string };

export const BLOCK_LABEL: Record<BlockType, string> = {
  heading: "Heading",
  paragraph: "Paragraph",
  bullets: "Bullet list",
  numbers: "Numbered list",
  callout: "Highlight box",
  quote: "Quote",
  table: "Table",
  image: "Picture",
  faq: "Question & answer",
  product: "Product link",
  reference: "Source link",
};

export const PRODUCT_TITLES: Record<ProductHandle, string> = {
  "mag-absorb-pro": "Mag Absorb Pro",
  "liver-biome-plus": "Liver Biome Plus",
  "ova-balance": "Ova Balance",
};

export function newBlockId(): string {
  return Math.random().toString(36).slice(2, 10);
}

export function makeBlock(type: BlockType): Block {
  const id = newBlockId();
  switch (type) {
    case "heading":
      return { id, type, level: 2, text: "" };
    case "paragraph":
      return { id, type, text: "" };
    case "bullets":
    case "numbers":
      return { id, type, items: [""] };
    case "callout":
      return { id, type, title: "Key takeaway", text: "" };
    case "quote":
      return { id, type, text: "", attribution: "" };
    case "table":
      return { id, type, headers: ["", ""], rows: [["", ""]] };
    case "image":
      return { id, type, url: "", alt: "", caption: "" };
    case "faq":
      return { id, type, question: "", answer: "" };
    case "product":
      return { id, type, handle: "mag-absorb-pro", blurb: "" };
    case "reference":
      return { id, type, label: "", url: "" };
  }
}

/** Defensive parse — DB JSON is untyped, drop anything we can't render. */
export function parseBlocks(raw: unknown): Block[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter((b): b is Block => {
    if (!b || typeof b !== "object") return false;
    const t = (b as { type?: unknown }).type;
    return typeof t === "string" && t in BLOCK_LABEL;
  });
}

/** Plain-text projection — used for AI SEO input and reading-time estimates. */
export function blocksToPlainText(blocks: Block[]): string {
  const out: string[] = [];
  for (const b of blocks) {
    switch (b.type) {
      case "heading":
        out.push(`\n## ${b.text}`);
        break;
      case "paragraph":
        out.push(b.text);
        break;
      case "bullets":
      case "numbers":
        out.push(b.items.map((i) => `- ${i}`).join("\n"));
        break;
      case "callout":
        out.push(`${b.title}: ${b.text}`);
        break;
      case "quote":
        out.push(`"${b.text}" ${b.attribution}`);
        break;
      case "table":
        out.push([b.headers.join(" | "), ...b.rows.map((r) => r.join(" | "))].join("\n"));
        break;
      case "image":
        if (b.caption) out.push(b.caption);
        break;
      case "faq":
        out.push(`Q: ${b.question}\nA: ${b.answer}`);
        break;
      case "product":
        out.push(`${PRODUCT_TITLES[b.handle]}: ${b.blurb}`);
        break;
      case "reference":
        out.push(`Reference: ${b.label}`);
        break;
    }
  }
  return out.filter(Boolean).join("\n\n");
}

export function estimateReadingTime(blocks: Block[]): string {
  const words = blocksToPlainText(blocks).split(/\s+/).filter(Boolean).length;
  return `${Math.max(1, Math.round(words / 200))} min read`;
}

export function slugify(input: string): string {
  return input
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[^a-z0-9\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .slice(0, 80);
}

/** FAQ blocks feed the FAQPage JSON-LD in addition to the explicit faqs field. */
export function faqsFromBlocks(blocks: Block[]): { q: string; a: string }[] {
  return blocks
    .filter((b): b is Extract<Block, { type: "faq" }> => b.type === "faq")
    .filter((b) => b.question.trim() && b.answer.trim())
    .map((b) => ({ q: b.question, a: b.answer }));
}
